import { useNavigate, useLocation } from "react-router-dom";
import { useState, useRef, useEffect } from "react";
import { toast } from "react-toastify";
import { verifyEmailOtp, verifyMobileOtp, verifyLoginOtp, resendOtp, resendRegisterOtp, logout } from "../../services/authService";
import { handleApiError } from "../../utils/handleApiError";
import { getUserLocation } from "../../utils/locationHelper";
import { useKyc } from "../../context/KycContext";
import logo from "../../assets/logo1.png";
import api from "../../services/api";

function OtpInputs({ value, onChange, length = 6, disabled }) {
  const inputs = useRef([]);
  return (
    <div className="flex gap-4 justify-center">
      {Array.from({ length }).map((_, i) => (
        <input
          key={i}
          ref={(el) => (inputs.current[i] = el)}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={value[i] || ""}
          onChange={(e) => {
            const val = e.target.value.replace(/[^0-9]/g, "");
            const newVal = value.split("");
            newVal[i] = val;
            onChange(newVal.join(""));
            if (val && i < length - 1) inputs.current[i + 1]?.focus();
          }}
          onKeyDown={(e) => {
            if (e.key === "Backspace" && !value[i] && i > 0) inputs.current[i - 1]?.focus();
          }}
          disabled={disabled}
          className="w-10 h-10 py-[16px] px-[12px] md:w-12 md:h-12 text-center border rounded-md text-lg bg-gray-50 focus:bg-white focus:border-purple-500 outline-none transition disabled:opacity-50"
        />
      ))}
    </div>
  );
}

function useResendTimer(initial = 30) {
  const [timer, setTimer] = useState(0);
  const intervalRef = useRef(null);
  const start = () => {
    setTimer(initial);
    clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      setTimer((t) => { if (t <= 1) { clearInterval(intervalRef.current); return 0; } return t - 1; });
    }, 1000);
  };
  useEffect(() => () => clearInterval(intervalRef.current), []);
  return { timer, start };
}

export default function VerifyCode() {
  const navigate = useNavigate();
  const location = useLocation();
  const email   = location.state?.email   || "";
  const mobile  = location.state?.mobile  || "";
  const user_id = location.state?.user_id || "";
  const source  = location.state?.source  || "register"; // "login" | "register"

  useEffect(() => {
    if (!location.state?.email) navigate("/dashboard", { replace: true });
  }, []);

  const [emailOtp, setEmailOtp] = useState("");
  const [mobileOtp, setMobileOtp] = useState("");
  const [loading, setLoading]   = useState(false);
  const emailTimer  = useResendTimer(30);
  const mobileTimer = useResendTimer(30);

  const handleResend = async () => {
    try {
      const fn = source === "login" ? resendOtp : resendRegisterOtp;
      await fn({ type: "email", user_id, email, mobile });
      toast.info("OTP resent to your email.");
      emailTimer.start();
    } catch (err) {
      handleApiError(err);
    }
  };

  const handleResendMobile = async () => {
    try {
      const fn = source === "login" ? resendOtp : resendRegisterOtp;
      await fn({ type: "mobile", user_id, email, mobile });
      toast.info("OTP resent to your mobile.");
      mobileTimer.start();
    } catch (err) {
      handleApiError(err);
    }
  };

  // Auto-send mobile OTP on mount for register - REMOVED
  // No auto-trigger on mount

  const { refreshKycStatus } = useKyc();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (emailOtp.length < 6) { toast.error("Enter 6-digit email OTP."); return; }
    if (source === "register" && mobile && mobileOtp.length < 6) {
      toast.error("Enter 6-digit mobile OTP."); return;
    }

    setLoading(true);
    try {
      if (source === "login") {
        const loc = await getUserLocation();
        if (!loc) return;
        const res = await verifyLoginOtp({
          user_id,
          otp: emailOtp,
          latitude: loc.latitude,
          longitude: loc.longitude,
        });
        const token = res.data?.token || res.data?.data?.token;
        if (token) localStorage.setItem("token", token);
      } else {
        // Register: call both APIs in parallel
        console.log("Email OTP API called");
        console.log("Mobile OTP API called");
        const [emailRes] = await Promise.all([
          verifyEmailOtp({ user_id, email, otp: emailOtp }),
          verifyMobileOtp({ user_id, mobile, otp: mobileOtp }),
        ]);
        const token = emailRes.data?.token || emailRes.data?.data?.token;
        if (token) localStorage.setItem("token", token);
      }

      toast.success("Verification successful!");

      const kycResult = await refreshKycStatus();
      if (kycResult?.kyc_submitted) {
        navigate("/dashboard");
      } else {
        navigate("/kyc");
      }
    } catch (err) {
      handleApiError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try { await logout(); } catch (_) {}
    finally {
      localStorage.removeItem("token");
      toast.success("Logged out successfully.");
      navigate("/");
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-md">
        <div className="flex flex-col items-center mb-6">
          <img src={logo} alt="Kresta Pay" className="w-24 mb-2" />
          <h2 className="text-[30px] font-bold text-[#34094D] mb-1">Verify Security Code</h2>
          <p className="text-[#4C444E] font-semibold text-center text-[16px] max-w-sm">
            We've sent a 6-digit verification code to your registered email.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-lg p-10 space-y-4">
          {/* Email OTP */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="font-bold text-[#4C444E] text-xs">EMAIL OTP</p>
                {email && <span className="text-sm text-[#191C1D] font-bold">{email}</span>}
              </div>
              <button
                type="button"
                className="ml-2 px-3 font-bold py-1 text-sm bg-[#4B2364] text-[#BB8DD5] rounded-[8px] disabled:opacity-50"
                disabled={loading || emailTimer.timer > 0}
                onClick={handleResend}
              >
                Send OTP
              </button>
            </div>
            <OtpInputs value={emailOtp} onChange={setEmailOtp} length={6} disabled={loading} />
            <div className="mt-2">
              {emailTimer.timer > 0 ? (
                <span className="text-xs text-gray-400">Resend in {emailTimer.timer}s</span>
              ) : (
                <button type="button" onClick={handleResend}
                  className="text-[#EA8534] text-xs font-bold tracking-wider mt-2">
                  RESEND OTP
                </button>
              )}
            </div>
          </div>

          {/* Mobile OTP */}
          {mobile && (
            <div className="pt-2 border-t border-gray-100">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <p className="font-bold text-[#4C444E] text-xs">MOBILE OTP</p>
                  <span className="text-sm text-[#191C1D] font-bold">{mobile}</span>
                </div>
                <button
                  type="button"
                  className="ml-2 px-3 font-bold py-1 text-sm bg-[#4B2364] text-[#BB8DD5] rounded-[8px] disabled:opacity-50"
                  disabled={loading || mobileTimer.timer > 0}
                  onClick={handleResendMobile}
                >
                  Send OTP
                </button>
              </div>
              <OtpInputs value={mobileOtp} onChange={setMobileOtp} length={6} disabled={loading} />
              <div className="mt-2">
                {mobileTimer.timer > 0 ? (
                  <span className="text-xs text-gray-400">Resend in {mobileTimer.timer}s</span>
                ) : (
                  <button type="button" onClick={handleResendMobile}
                    className="text-[#EA8534] text-xs font-bold tracking-wider mt-2">
                    RESEND OTP
                  </button>
                )}
              </div>
            </div>
          )}

          <button type="submit" disabled={loading}
            className={`w-full py-3.5 rounded-xl text-white text-[16px] font-bold bg-[linear-gradient(97.63deg,_#EA8534_0%,_#A0111A_100%)] shadow-lg flex items-center justify-center gap-2 transition-all ${loading ? "opacity-70 cursor-not-allowed" : "hover:opacity-90 cursor-pointer"}`}>
            {loading && <svg className="animate-spin" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2"><circle cx="12" cy="12" r="10" strokeOpacity="0.3" /><path d="M12 2a10 10 0 0 1 10 10" /></svg>}
            {loading ? "Verifying…" : "Continue"}
          </button>

          <div className="text-xs text-center font-bold text-[#4C444E]">
            Having trouble?{" "}
            <span className="text-[#34094D] font-bold cursor-pointer hover:underline">Contact Support</span>
          </div>
        </form>

        <div className="text-xs text-center mt-4 text-[#4C444E] font-bold cursor-pointer hover:text-gray-600 transition"
          onClick={handleLogout}>
          ← Back to Merchant Login
        </div>
      </div>
    </div>
  );
}
